import {
  Check,
  Download,
  Edit,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import {
  useDeleteOrderDocument,
  useOrderDocuments,
  useUpdateOrderDocument,
  useUploadOrderDocument,
} from '@my/tms-api/hooks';
import type { OrderDocument, OrderDocumentTypeValue } from '@my/tms-api';
import {
  ConfirmDialog,
  ErrorBanner,
  Loading,
  SelectInput,
  TextInput,
  showToast,
} from '@my/ui';
import { formatTaskDate } from '../lib/dataCells';
import { formatFileSize } from '../lib/format';
import {
  ORDER_DOCUMENT_TYPE_LABELS,
  ORDER_DOCUMENT_TYPE_OPTIONS,
} from '../lib/documentTypeOptions';
import { ReadOnlyField } from './inputs';

interface DocumentsPanelProps {
  /** Null while on the create flow — disables uploads and empties the list. */
  orderId: string | null;
}

/**
 * File attachments on the order — POD scans, customs paperwork,
 * weighbridge tickets, photos, etc. Two-pane master-detail layout.
 * Wired to the BE via the four document hooks; the list DTO carries a
 * short-lived SAS `downloadUrl` so open/download work directly.
 */
export function DocumentsPanel({ orderId }: DocumentsPanelProps) {
  const listQuery = useOrderDocuments(orderId);
  const uploadMutation = useUploadOrderDocument();
  const updateMutation = useUpdateOrderDocument();
  const deleteMutation = useDeleteOrderDocument();

  const isBusy =
    uploadMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const canUpload = orderId !== null && !isBusy;

  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Memoised because two effects below depend on it: `?? []` hands back a fresh
  // array on every render while the query is still loading, which would re-run
  // both of them on every render.
  const docs = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const [selectedDocId, setSelectedDocId] = useState<string>('');
  useEffect(() => {
    if (docs.length === 0) {
      if (selectedDocId !== '') setSelectedDocId('');
      return;
    }
    const stillExists = docs.some((d) => d.id === selectedDocId);
    if (!stillExists) {
      setSelectedDocId(docs[0]?.id ?? '');
    }
  }, [docs, selectedDocId]);
  const selectedDoc = docs.find((d) => d.id === selectedDocId) ?? null;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{
    description: string;
    type: OrderDocumentTypeValue;
  }>({ description: '', type: 'Other' });

  const [pendingDelete, setPendingDelete] = useState<OrderDocument | null>(null);

  /**
   * A just-uploaded document waiting for the list to refetch so its editor can
   * be opened on it.
   *
   * <p>
   * Held as an id rather than opening the editor inline at upload time because
   * the row does not exist yet: the list is invalidated, not patched, so for a
   * beat `docs` has no such document and the selection effect above would push
   * the selection back to `docs[0]`. Waiting for the row to arrive is the only
   * version of this that survives uploading a SECOND document.
   * </p>
   */
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingEditId) return;
    const arrived = docs.find((d) => d.id === pendingEditId);
    if (!arrived) return;
    setSelectedDocId(arrived.id);
    setEditingId(arrived.id);
    setEditDraft({ description: arrived.description ?? '', type: arrived.type });
    setPendingEditId(null);
  }, [docs, pendingEditId]);

  const uploadFiles = async (files: FileList | null) => {
    if (!orderId) {
      showToast({
        type: 'error',
        message: 'Save the order first — uploads need an order id to attach to.',
      });
      return;
    }
    if (!files || files.length === 0) return;
    const picked = Array.from(files);
    let success = 0;
    let firstId: string | null = null;
    for (const file of picked) {
      try {
        const id = await uploadMutation.mutateAsync({
          orderId,
          file,
          type: 'Other',
          description: null,
        });
        if (firstId === null) firstId = id;
        success += 1;
      } catch (err) {
        showToast({
          type: 'error',
          message: err instanceof Error ? err.message : `Failed to upload ${file.name}.`,
        });
      }
    }
    if (success > 0) {
      showToast({
        type: 'success',
        message: success === 1 ? 'Document uploaded.' : `${success} documents uploaded.`,
      });
    }
    // Everything lands as `Other`, which is almost never what it is — so open the
    // editor on it straight away rather than leaving the operator to notice.
    // Only for a single file: opening one form out of five picked would be a
    // guess at which one the operator meant, and the other four would keep the
    // wrong type anyway.
    if (picked.length === 1 && firstId) setPendingEditId(firstId);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (canUpload) setDragActive(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!canUpload) return;
    await uploadFiles(e.dataTransfer.files);
  };

  const startEdit = (doc: OrderDocument) => {
    setEditingId(doc.id);
    setEditDraft({ description: doc.description ?? '', type: doc.type });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ description: '', type: 'Other' });
  };

  const handleEditSave = async () => {
    if (!editingId || !orderId) return;
    try {
      await updateMutation.mutateAsync({
        orderId,
        body: {
          documentId: editingId,
          description: editDraft.description.trim() || null,
          type: editDraft.type,
          // Preserve existing task scoping so an edit doesn't accidentally detach a task-bound doc.
          orderTaskId: selectedDoc?.orderTaskId ?? null,
        },
      });
      showToast({ type: 'success', message: 'Document updated.' });
      cancelEdit();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update document.',
      });
    }
  };

  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target || !orderId) return;
    try {
      await deleteMutation.mutateAsync({ orderId, documentId: target.id });
      showToast({ type: 'success', message: 'Document deleted.' });
      if (editingId === target.id) cancelEdit();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete document.',
      });
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <section className="flex h-full flex-col rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-2">
        <h2 className="text-sm font-semibold text-slate-900">
          Documents <span className="text-slate-400">({docs.length})</span>
        </h2>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canUpload}
          className="flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </header>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          void uploadFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <div className="grid min-h-0 flex-1 grid-cols-1 sm:grid-cols-5">
        {/* Left: drop zone + filename list */}
        <div className="flex flex-col border-slate-200 px-3 py-2 sm:col-span-2 sm:border-r">
          <div
            onClick={() => (canUpload ? fileInputRef.current?.click() : undefined)}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={
              'flex shrink-0 items-center justify-center gap-1.5 rounded-md border-2 border-dashed px-2 py-1.5 text-xs font-medium transition-colors ' +
              (canUpload ? 'cursor-pointer ' : 'cursor-not-allowed opacity-60 ') +
              (dragActive
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-slate-300 bg-slate-50 text-slate-600 hover:border-slate-400')
            }
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {orderId === null ? 'Save order to enable uploads' : 'Drop or click to upload'}
          </div>

          {listQuery.isPending && orderId !== null && (
            <div className="mt-2">
              <Loading label="Loading documents" />
            </div>
          )}
          {listQuery.isError && (
            <div className="mt-2">
              <ErrorBanner title="Failed to load documents" message={listQuery.error.message} />
            </div>
          )}
          {listQuery.isSuccess && docs.length === 0 && (
            <p className="mt-3 text-center text-[11px] italic text-slate-400">
              No documents on this order yet.
            </p>
          )}
          {docs.length > 0 && (
            <ul className="mt-2 space-y-0.5 lg:max-h-20 lg:min-h-0 lg:overflow-y-auto">
              {docs.map((doc) => {
                const active = doc.id === selectedDocId;
                return (
                  <li key={doc.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedDocId(doc.id)}
                      className={
                        'flex w-full items-center gap-1.5 rounded-sm px-2 py-1 text-left text-xs transition-colors ' +
                        (active
                          ? 'bg-blue-50 font-medium text-blue-700'
                          : 'text-slate-700 hover:bg-slate-100')
                      }
                    >
                      <FileText className="h-3 w-3 shrink-0 text-slate-400" />
                      <span className="truncate">{doc.fileName}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Right: detail / editor */}
        <div className="px-3 py-2 sm:col-span-3 lg:min-h-0 lg:overflow-y-auto">
          {selectedDoc ? (
            editingId === selectedDoc.id ? (
              <DocumentEditor
                doc={selectedDoc}
                draft={editDraft}
                onChange={setEditDraft}
                onSave={handleEditSave}
                onCancel={cancelEdit}
                onDelete={() => setPendingDelete(selectedDoc)}
                isBusy={isBusy}
              />
            ) : (
              <DocumentDetail doc={selectedDoc} onEdit={() => startEdit(selectedDoc)} />
            )
          ) : (
            <p className="text-xs italic text-slate-400">No document selected.</p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title="Delete document"
        message={pendingDelete ? `Delete "${pendingDelete.fileName}"?` : null}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </section>
  );
}

/** Right-pane read-only detail view for one document. */
function DocumentDetail({ doc, onEdit }: { doc: OrderDocument; onEdit: () => void }) {
  const typeLabel = ORDER_DOCUMENT_TYPE_LABELS[doc.type];
  const uploadedDate = doc.createdUtc.slice(0, 10);
  const hasDownload = !!doc.downloadUrl;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-1 flex items-start justify-between gap-1">
        {hasDownload ? (
          <a
            href={doc.downloadUrl ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${doc.fileName}`}
            className="min-w-0 truncate text-sm font-semibold text-blue-600 hover:underline"
          >
            {doc.fileName}
          </a>
        ) : (
          <p className="min-w-0 truncate text-sm font-semibold text-slate-900">{doc.fileName}</p>
        )}
        <span className="inline-flex shrink-0 items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
          {typeLabel}
        </span>
      </div>

      <div className="flex-1 space-y-0">
        <ReadOnlyField label="Size" value={formatFileSize(doc.fileSizeBytes)} />
        <ReadOnlyField label="Uploaded" value={formatTaskDate(uploadedDate)} />
        {doc.description && <ReadOnlyField label="Description" value={doc.description} />}
      </div>

      <div className="mt-0 flex justify-end gap-0">
        {hasDownload && (
          <>
            <a
              href={doc.downloadUrl ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${doc.fileName} in new tab`}
              title="Open in new tab"
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-slate-600 hover:bg-slate-100"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <a
              href={doc.downloadUrl ?? '#'}
              download={doc.fileName}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Download ${doc.fileName}`}
              title="Download"
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-slate-600 hover:bg-slate-100"
            >
              <Download className="h-3.5 w-3.5" />
            </a>
          </>
        )}
        <button
          type="button"
          aria-label={`Edit ${doc.fileName}`}
          title="Edit"
          onClick={onEdit}
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

interface DocumentEditorProps {
  doc: OrderDocument;
  draft: { description: string; type: OrderDocumentTypeValue };
  onChange: (next: { description: string; type: OrderDocumentTypeValue }) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isBusy: boolean;
}

/** Inline metadata editor (type + description). Replaces the detail view while editing. */
function DocumentEditor({
  doc,
  draft,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: DocumentEditorProps) {
  return (
    <div className="flex h-full flex-col space-y-3">
      <div className="flex items-center gap-2">
        <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
        <p className="truncate text-sm font-semibold text-slate-900" title={doc.fileName}>
          {doc.fileName}
        </p>
      </div>
      <div className="space-y-3">
        <SelectInput
          label="Type"
          name="document-type"
          value={draft.type}
          options={ORDER_DOCUMENT_TYPE_OPTIONS}
          disabled={isBusy}
          onChange={(e) =>
            onChange({ ...draft, type: e.target.value as OrderDocumentTypeValue })
          }
        />
        <TextInput
          label="Description"
          name="document-description"
          value={draft.description}
          maxLength={1024}
          disabled={isBusy}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
        />
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          aria-label="Delete document"
          onClick={onDelete}
          disabled={isBusy}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Cancel"
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Save"
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
